import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Wrench, AlertTriangle, Calendar, ClipboardList, ClipboardCheck, Users, Truck, TruckIcon, Wand2, Loader2, ExternalLink, CheckCircle2, ScanEye, Copy } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { format, startOfWeek, endOfWeek, startOfDay, endOfDay, parseISO, addHours, addDays } from "date-fns";
import { WorkOrder, Tractor, DriverDayOff } from "@/types/tms";
import { NeededDocumentationTab } from "@/components/orders/NeededDocumentationTab";
import { useFleetSchedulerAssignment } from "@/hooks/useFleetSchedulerAssignment";
import { useTurnTime } from "@/hooks/useTurnTime";
import { useTenant } from "@/hooks/useTenant";
import { useDeliveredOrdersDocumentation } from "@/hooks/useDeliveredOrdersDocumentation";
import { OrderWithRelations } from "@/components/schedule/types";
import { TractorWithHome, DriverWithHome } from "@/types/resources";
import { useStarredOrderIds } from "@/hooks/useOrderStars";
import { OrderDetailSheet } from "@/components/orders/OrderDetailSheet";
import { OrderEditDialog } from "@/components/orders/OrderEditDialog";
import { BolViewerDialog } from "@/components/orders/BolViewerDialog";
import { TenderViewerDialog } from "@/components/orders/TenderViewerDialog";
import { OrderTableRow } from "@/components/orders/OrderTableRow";
import { OrderCard } from "@/components/orders/OrderCard";
import { ResponsiveTableShell } from "@/components/ui/responsive-table-shell";
import { LateOrdersTable, LateOrdersBody, type LateOrder } from "@/components/dashboard/LateOrdersTable";
import { DashboardPagination, usePagination } from "@/components/dashboard/DashboardPagination";
import { useOrderLegSummaries } from "@/hooks/useOrderLegSummaries";
import { DuplicateOrdersReview } from "@/components/dashboard/DuplicateOrdersReview";
import { useDuplicateOrderPairs } from "@/hooks/useDuplicateOrderPairs";
import { latestReadyTime, latestDeliveryTime } from "@/lib/order-time";
import { getOperationalDayBounds } from "@/lib/timezone";
import { Order, Customer, Tractor as TractorType, Driver, Location } from "@/types/tms";
import {
  ORDERS_WITH_LEGS_SELECT,
  ORDERS_WITH_LEGS_VIEW,
  mapAndHydrateLegRows,
} from "@/lib/orders-with-legs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

interface ServiceHoldEntry {
  id: string;
  tractorName: string;
  description: string | null;
  startTime: string;
  endTime: string;
  source: "local";
}


type StarredOrderWithRelations = Order & {
  billing_party: Customer | null;
  assigned_tractor: TractorType | null;
  assigned_operator: Driver | null;
  assigned_operator_2: Driver | null;
};

function ServiceHoldRow({ entry, timeLabel, timeField, variant }: {
  entry: ServiceHoldEntry;
  timeLabel: string;
  timeField: "startTime" | "endTime";
  variant: "today" | "upcoming";
}) {
  const bgClass = variant === "today"
    ? "bg-tractor-in-service/10 border-tractor-in-service/20"
    : "bg-muted/50 border-border";


  return (
    <div className={`flex items-start justify-between p-3 rounded-lg border ${bgClass}`}>
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <p className="font-medium">{entry.tractorName}</p>
        </div>
        <p className="text-sm text-muted-foreground">{entry.description || "Maintenance"}</p>
      </div>
      <div className="text-right text-sm">
        <p className="text-muted-foreground">{timeLabel}</p>
        <p className="font-medium">{format(parseISO(entry[timeField]), "EEE, MMM d 'at' h:mm a")}</p>
      </div>
    </div>
  );
}

type InsightKey = "manual-review" | "unbillable" | "stale" | "needs-truck" | "duplicates";

export default function Dashboard() {
  const [activeInsight, setActiveInsight] = useState<InsightKey>("manual-review");
  const navigate = useNavigate();
  const { tenantId, tenantReady } = useTenant();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const markDeliveredMutation = useMutation({
    mutationFn: async (order: LateOrder) => {
      const nowIso = new Date().toISOString();
      const updatePayload: Record<string, unknown> = {
        status: "delivered",
        actual_arrival_time: nowIso,
        eapi_eta: null,
      };
      if (order.status !== "in_transit") {
        updatePayload.actual_departure_time = nowIso;
      }
      if (!tenantId) throw new Error("Missing tenant");
      const { error } = await supabase
        .from("orders")
        .update(updatePayload)
        .eq("id", order.id)
        .eq("tenant_id", tenantId);
      if (error) throw error;
      await supabase.from("order_change_log").insert({
        order_id: order.id,
        tenant_id: tenantId,
        field_name: "status",
        old_value: order.status,
        new_value: "delivered",
        changed_by: user?.email ?? "Unknown",
      });
      return order.id;
    },
    onSuccess: () => {
      toast.success("Order marked as delivered");
      queryClient.invalidateQueries({ queryKey: ["manual-review-orders"] });
      queryClient.invalidateQueries({ queryKey: ["late-arrivals"] });
      queryClient.invalidateQueries({ queryKey: ["stale-orders"] });
    },
    onError: (err: Error) => {
      toast.error(err.message || "Failed to mark delivered");
    },
  });

  const { data: starredIds } = useStarredOrderIds();
  const starredIdArray = useMemo(
    () => Array.from(starredIds ?? []).sort(),
    [starredIds]
  );

  const { data: starredOrders } = useQuery({
    queryKey: ["starred-orders", tenantId, starredIdArray],
    enabled: tenantReady && !!tenantId && starredIdArray.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`*, billing_party:customers(*), assigned_tractor:tractors(*), assigned_operator:drivers!orders_assigned_operator_id_fkey(*), assigned_operator_2:drivers!orders_assigned_operator_2_id_fkey(*)`)
        .in("id", starredIdArray)
        .eq("tenant_id", tenantId!)
        .order("ready_time", { ascending: true });
      if (error) throw error;
      return data as StarredOrderWithRelations[];
    },
  });
  // Locations are needed by OrderTableRow / LateOrdersTable to render
  // ready/delivery times in the order's location timezone.
  const { data: locations } = useQuery({
    queryKey: ["dashboard-locations"],
    enabled: tenantReady,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("locations")
        .select("*")
        .eq("archived", false);
      if (error) throw error;
      return (data ?? []) as Location[];
    },
  });
  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);
  const weekStart = startOfWeek(now, { weekStartsOn: 1 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
  const { turnTimeMinutes } = useTurnTime();
  const { assignPlus: assign, isAssigningPlus: isAssigning } = useFleetSchedulerAssignment(weekStart, turnTimeMinutes);

  // Today's orders with full details
  const { data: todayOrders } = useQuery({
    queryKey: ["today-orders-full", tenantId, todayStart.toISOString()],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      // Phase 4: per-leg rows so multi-leg trips count once per leg in the dashboard.
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDERS_WITH_LEGS_SELECT)
        .gte("ready_time", todayStart.toISOString())
        .lte("ready_time", todayEnd.toISOString())
        .eq("is_work_order", false)
        .order("ready_time");
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
  });

  const assignedOrders = todayOrders?.filter((o) => o.assigned_operator_id).length ?? 0;
  const unassignedOrders = (todayOrders?.length ?? 0) - assignedOrders;

  // Available operators
  const { data: availableOperators } = useQuery({
    queryKey: ["available-operators-today", tenantId, todayStart.toISOString()],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      const [driversRes, daysOffRes] = await Promise.all([
        supabase.from("drivers").select("*", { count: "exact", head: true }),
        supabase.from("driver_days_off").select("driver_id").eq("date", format(now, "yyyy-MM-dd")),
      ]);
      if (driversRes.error) throw driversRes.error;
      if (daysOffRes.error) throw daysOffRes.error;
      const total = driversRes.count ?? 0;
      const off = daysOffRes.data?.length ?? 0;
      return { total, off, available: total - off };
    },
  });

  // Available tractors
  const { data: availableTractors } = useQuery({
    queryKey: ["available-tractors-today", tenantId],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      const { count, error } = await supabase.from("tractors").select("*", { count: "exact", head: true });
      if (error) throw error;
      return { total: count ?? 0, available: count ?? 0 };
    },
  });

  const sevenDaysEnd = endOfDay(addDays(now, 6));

  const { data: workOrders } = useQuery({
    queryKey: ["work-orders-upcoming", tenantId, todayStart.toISOString(), sevenDaysEnd.toISOString()],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("work_orders")
        .select("*")
        .gte("start_time", todayStart.toISOString())
        .lte("start_time", sevenDaysEnd.toISOString())
        .order("start_time", { ascending: true });
      if (error) throw error;
      return data as WorkOrder[];
    },
  });


  const { data: tractorsWithHome } = useQuery({
    queryKey: ["tractors-with-home-base", tenantId],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from("tractors").select(`*, home_base:locations(*)`).order("name");
      if (error) throw error;
      return data as TractorWithHome[];
    },
  });

  const { data: driversWithFlights } = useQuery({
    queryKey: ["drivers-with-flights", tenantId],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase.from("drivers").select(`*, home_base:locations(*), flight:flights(*)`).order("name");
      if (error) throw error;
      return data as DriverWithHome[];
    },
  });

  const { data: weekOrders } = useQuery({
    queryKey: ["schedule-orders", tenantId, weekStart.toISOString()],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      // Phase 4: per-leg rows feed the auto-assignment workflow with leg granularity.
      const { data, error } = await supabase
        .from(ORDERS_WITH_LEGS_VIEW)
        .select(ORDERS_WITH_LEGS_SELECT)
        .gte("ready_time", weekStart.toISOString())
        .lt("ready_time", weekEnd.toISOString())
        .order("ready_time")
        .limit(3000);
      if (error) throw error;
      return mapAndHydrateLegRows(data);
    },
  });

  const { data: daysOff } = useQuery({
    queryKey: ["driver-days-off", tenantId, weekStart.toISOString()],
    enabled: tenantReady && !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("driver_days_off")
        .select("*")
        .gte("date", format(weekStart, "yyyy-MM-dd"))
        .lt("date", format(weekEnd, "yyyy-MM-dd"));
      if (error) throw error;
      return data as DriverDayOff[];
    },
  });

  const tractors = tractorsWithHome as Pick<Tractor, "id" | "name">[] | undefined;

  // Local work orders
  const localHolds: ServiceHoldEntry[] = (workOrders ?? []).map((wo) => {
    const tractor = tractors?.find((t) => t.id === wo.tractor_id);
    const endTime = addHours(new Date(wo.start_time), wo.duration_hours);
    return {
      id: wo.id,
      tractorName: tractor?.name ?? "Unknown Tractor",
      description: wo.description,
      startTime: wo.start_time,
      endTime: endTime.toISOString(),
      source: "local" as const,
    };
  });

  const serviceHoldEntries = [...localHolds].sort(
    (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  );

  const todayServiceHolds = serviceHoldEntries.filter((entry) => {
    const start = parseISO(entry.startTime);
    const end = parseISO(entry.endTime);
    return start < todayEnd && end > todayStart;
  });

  const thisWeekServiceHolds = serviceHoldEntries.filter((entry) => {
    const start = parseISO(entry.startTime);
    const end = parseISO(entry.endTime);
    const overlapsToday = start < todayEnd && end > todayStart;
    return !overlapsToday;
  });

  const operators = availableOperators?.available ?? 0;
  const tractorsAvailable = availableTractors?.available ?? 0;

  const handleAutoAssign = () => {
    if (!weekOrders || !tractorsWithHome || !driversWithFlights) return;
    const todayOnly = weekOrders.filter((o) => {
      const rt = new Date(o.ready_time);
      return rt >= todayStart && rt <= todayEnd;
    });
    assign({ orders: todayOnly, tractors: tractorsWithHome, drivers: driversWithFlights, workOrders: workOrders || [], daysOff: daysOff || [] });
  };

  // --- Insight card counts (same source as the Unbillable / Missing BOL tabs) ---
  const {
    isLoading: docsLoading,
    unbillableOrders: insightUnbillable,
  } = useDeliveredOrdersDocumentation();
  const unbillableCount = insightUnbillable.length;
  // Before the tenant resolves the queries are disabled (not "loading"), so
  // treat that as loading too.
  const docsPending = docsLoading || !tenantReady;
  const unbillableDisplay = docsPending ? "—" : unbillableCount;

  const [previewOrder, setPreviewOrder] = useState<StarredOrderWithRelations | null>(null);
  const [editOrder, setEditOrder] = useState<StarredOrderWithRelations | null>(null);
  const handleStarredRowClick = useCallback((order: StarredOrderWithRelations) => {
    setPreviewOrder(order);
  }, []);

  // Re-render every 60s so lateness filtering refreshes without manual reload.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setNowTick((n) => n + 1), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // "Today" is the Central (planning) day so dispatchers everywhere see the
  // same late/stale buckets at the same UTC instant (docs/TIMEZONES.md).
  const { todayStartIso, todayEndIso } = useMemo(() => {
    void nowTick;
    const bounds = getOperationalDayBounds();
    return { todayStartIso: bounds.startIso, todayEndIso: bounds.endIso };
  }, [nowTick]);

  // Late departures: status=confirmed, latest ready time has passed and falls in today.
  const { data: lateDepartureCandidates } = useQuery({
    queryKey: ["late-departures", tenantId, todayStartIso, todayEndIso],
    enabled: tenantReady && !!tenantId,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`*, billing_party:customers(*), assigned_tractor:tractors(*), assigned_operator:drivers!orders_assigned_operator_id_fkey(*), assigned_operator_2:drivers!orders_assigned_operator_2_id_fkey(*)`)
        .eq("tenant_id", tenantId!)
        .eq("is_work_order", false)
        .eq("status", "confirmed")
        .gte("ready_time", todayStartIso)
        .lte("ready_time", todayEndIso)
        .order("ready_time", { ascending: true })
        .limit(500);
      if (error) throw error;
      return data as LateOrder[];
    },
  });

  const lateDepartures = useMemo(() => {
    void nowTick;
    const cutoff = Date.now();
    const dayStart = new Date(todayStartIso).getTime();
    const dayEnd = new Date(todayEndIso).getTime();
    return (lateDepartureCandidates ?? [])
      .filter((o) => {
        const latest = latestReadyTime(o);
        if (!latest) return false;
        const t = latest.getTime();
        return t < cutoff && t >= dayStart && t <= dayEnd;
      })
      .sort((a, b) => {
        const ta = latestReadyTime(a)?.getTime() ?? 0;
        const tb = latestReadyTime(b)?.getTime() ?? 0;
        return ta - tb;
      });
  }, [lateDepartureCandidates, nowTick, todayStartIso, todayEndIso]);

  // Late arrivals: status=in_transit, latest delivery time has passed and falls in today.
  const { data: lateArrivalCandidates } = useQuery({
    queryKey: ["late-arrivals", tenantId, todayStartIso, todayEndIso],
    enabled: tenantReady && !!tenantId,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`*, billing_party:customers(*), assigned_tractor:tractors(*), assigned_operator:drivers!orders_assigned_operator_id_fkey(*), assigned_operator_2:drivers!orders_assigned_operator_2_id_fkey(*)`)
        .eq("tenant_id", tenantId!)
        .eq("is_work_order", false)
        .eq("status", "in_transit")
        .gte("requested_delivery_time", todayStartIso)
        .lte("requested_delivery_time", todayEndIso)
        .order("requested_delivery_time", { ascending: true })
        .limit(500);
      if (error) throw error;
      return data as LateOrder[];
    },
  });

  const lateArrivals = useMemo(() => {
    void nowTick;
    const cutoff = Date.now();
    const dayStart = new Date(todayStartIso).getTime();
    const dayEnd = new Date(todayEndIso).getTime();
    return (lateArrivalCandidates ?? [])
      .filter((o) => {
        const latest = latestDeliveryTime(o);
        if (!latest) return false;
        const t = latest.getTime();
        return t < cutoff && t >= dayStart && t <= dayEnd;
      })
      .sort((a, b) => {
        const ta = latestDeliveryTime(a)?.getTime() ?? 0;
        const tb = latestDeliveryTime(b)?.getTime() ?? 0;
        return ta - tb;
      });
  }, [lateArrivalCandidates, nowTick, todayStartIso, todayEndIso]);

  // Rolling 24h cutoff that slides with `nowTick` so stale and manual-review
  // buckets re-evaluate every minute.
  const twentyFourHoursAgoIso = useMemo(() => {
    void nowTick;
    return new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  }, [nowTick]);

  const nowIso = useMemo(() => {
    void nowTick;
    return new Date().toISOString();
  }, [nowTick]);

  // Stale orders: non-terminal status, latest delivery time is more than 24h ago.
  const { data: staleCandidates } = useQuery({
    queryKey: ["stale-orders", tenantId, twentyFourHoursAgoIso],
    enabled: tenantReady && !!tenantId,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`*, billing_party:customers(*), assigned_tractor:tractors(*), assigned_operator:drivers!orders_assigned_operator_id_fkey(*), assigned_operator_2:drivers!orders_assigned_operator_2_id_fkey(*)`)
        .eq("tenant_id", tenantId!)
        .eq("is_work_order", false)
        .not("status", "in", "(delivered,canceled)")
        .lt("requested_delivery_time", twentyFourHoursAgoIso)
        .order("requested_delivery_time", { ascending: true })
        .limit(500);
      if (error) throw error;
      return data as LateOrder[];
    },
  });

  const staleOrders = useMemo(() => {
    void nowTick;
    const cutoff = Date.now() - 24 * 60 * 60 * 1000;
    return (staleCandidates ?? [])
      .filter((o) => {
        const ld = latestDeliveryTime(o)?.getTime();
        return typeof ld === "number" && ld < cutoff;
      })
      .sort((a, b) => {
        const ta = latestDeliveryTime(a)?.getTime() ?? 0;
        const tb = latestDeliveryTime(b)?.getTime() ?? 0;
        return ta - tb;
      });
  }, [staleCandidates, nowTick]);

  // Manual review required: trip_type=Manual, in_transit, delivery time within last 24h.
  const { data: manualReviewCandidates } = useQuery({
    queryKey: ["manual-review-orders", tenantId, twentyFourHoursAgoIso, nowIso],
    enabled: tenantReady && !!tenantId,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("orders")
        .select(`*, billing_party:customers(*), assigned_tractor:tractors(*), assigned_operator:drivers!orders_assigned_operator_id_fkey(*), assigned_operator_2:drivers!orders_assigned_operator_2_id_fkey(*)`)
        .eq("tenant_id", tenantId!)
        .eq("is_work_order", false)
        .eq("status", "in_transit")
        .eq("trip_type", "Manual")
        .gte("requested_delivery_time", twentyFourHoursAgoIso)
        .lte("requested_delivery_time", nowIso)
        .order("requested_delivery_time", { ascending: true })
        .limit(500);
      if (error) throw error;
      return data as LateOrder[];
    },
  });

  const manualReviewOrders = useMemo(() => {
    void nowTick;
    const now = Date.now();
    const cutoff = now - 24 * 60 * 60 * 1000;
    return (manualReviewCandidates ?? [])
      .filter((o) => {
        const ld = latestDeliveryTime(o)?.getTime();
        return typeof ld === "number" && ld >= cutoff && ld <= now;
      })
      .sort((a, b) => {
        const ta = latestDeliveryTime(a)?.getTime() ?? 0;
        const tb = latestDeliveryTime(b)?.getTime() ?? 0;
        return ta - tb;
      });
  }, [manualReviewCandidates, nowTick]);

  const starredPagination = usePagination(starredOrders ?? []);
  // One batched leg-status lookup for the subscribed rows on screen, so
  // multi-leg orders show the leg-derived rollup chip (e.g. Partially Confirmed).
  const { legsFor: starredLegsFor } = useOrderLegSummaries(
    starredPagination.pageRows.map((o: { id: string }) => o.id),
  );

  // Needs Truck Assignment — orders in a non-terminal, pre-transit status
  // that have no tractor assigned.
  const { data: needsTruckOrdersRaw } = useQuery({
    queryKey: ["needs-truck-orders", tenantId],
    enabled: tenantReady && !!tenantId,
    refetchInterval: 60_000,
    queryFn: async () => {
      const now = new Date();
      const from = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      const to = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from("orders")
        .select(`*, billing_party:customers(*), assigned_tractor:tractors(*), assigned_operator:drivers!orders_assigned_operator_id_fkey(*), assigned_operator_2:drivers!orders_assigned_operator_2_id_fkey(*)`)
        .eq("tenant_id", tenantId!)
        .eq("is_work_order", false)
        .eq("status", "booked")
        .is("assigned_tractor_id", null)
        .gte("ready_time", from)
        .lte("ready_time", to)
        .order("ready_time", { ascending: true })
        .limit(500);
      if (error) throw error;
      return data as LateOrder[];
    },
  });
  const needsTruckOrders = needsTruckOrdersRaw ?? [];
  const needsTruckCount = needsTruckOrders.length;

  const { data: duplicatePairs } = useDuplicateOrderPairs();
  const duplicateCount = duplicatePairs?.length ?? 0;

  const insightBody = (key: InsightKey) => {
    if (key === "duplicates") return <DuplicateOrdersReview />;
    if (key === "unbillable") return <NeededDocumentationTab />;
    if (key === "needs-truck") {
      return (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            Booked orders with a ready time from 24 hours ago through the next 7 days that don't have a truck assigned yet.
          </p>
          <LateOrdersBody
            variant="stale"
            orders={needsTruckOrders}
            onRowClick={(o) => setPreviewOrder(o as unknown as StarredOrderWithRelations)}
            selectedId={previewOrder?.id}
            columns={1}
            emptyText="All orders have a truck assigned"
          />
        </div>
      );
    }
    if (key === "stale") {
      return (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            These orders have not been marked Delivered or Cancelled and the latest delivery time is over 24 hours ago.
          </p>
          <LateOrdersBody
            variant="stale"
            orders={staleOrders}
            onRowClick={(o) => setPreviewOrder(o as unknown as StarredOrderWithRelations)}
            selectedId={previewOrder?.id}
            columns={1}
            emptyText="No stale orders"
          />
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {manualReviewOrders.length > 0 ? (
          <p className="text-sm text-muted-foreground">
            These are Manual trips that are past delivery time in the the last 24 hours. Mark them as Delivered if the truck and/or trailer has successfully reached the destination.
          </p>
        ) : null}
        <LateOrdersBody
          variant="manual-review"
          orders={manualReviewOrders}
          onRowClick={(o) => setPreviewOrder(o as unknown as StarredOrderWithRelations)}
          selectedId={previewOrder?.id}
          columns={1}
          emptyText="No orders performed in Manual need review"
          showOperators
          onMarkDelivered={(o) => markDeliveredMutation.mutate(o)}
          markDeliveredPendingId={markDeliveredMutation.isPending ? (markDeliveredMutation.variables as LateOrder | undefined)?.id ?? null : null}
        />
      </div>
    );
  };

  const insightItems: Array<{
    key: InsightKey;
    /** Rendered as-is; "—" while the count is still loading. */
    count: number | string;
    label: string;
    icon: typeof ClipboardCheck;
    iconBg: string;
    iconColor: string;
  }> = [
    {
      key: "duplicates",
      count: duplicateCount,
      label: "Duplicate Orders",
      icon: Copy,
      iconBg: duplicateCount > 0 ? "bg-amber-500/10" : "bg-muted",
      iconColor: duplicateCount > 0 ? "text-amber-500" : "text-muted-foreground",
    },
    {
      key: "needs-truck",
      count: needsTruckCount,
      label: "Needs Truck Assignment",
      icon: TruckIcon,
      // Red styling only when there is something to act on.
      iconBg: needsTruckCount > 0 ? "bg-destructive/10" : "bg-muted",
      iconColor: needsTruckCount > 0 ? "text-destructive" : "text-muted-foreground",
    },
    { key: "manual-review", count: manualReviewOrders.length, label: "Manual Trips", icon: AlertTriangle, iconBg: "bg-destructive/10", iconColor: "text-destructive" },
    { key: "unbillable", count: unbillableDisplay, label: "Unbillable Orders", icon: AlertTriangle, iconBg: "bg-destructive/10", iconColor: "text-destructive" },
    { key: "stale", count: staleOrders.length, label: "Stale Orders", icon: AlertTriangle, iconBg: "bg-[hsl(var(--aurora-status-warning))]/10", iconColor: "text-[hsl(var(--aurora-status-warning))]" },
  ];

  return (
    <AppLayout title="Dashboard">
      <div className="flex flex-col gap-6 animate-fade-in min-h-full">
        {/* Insight Cards + Table */}
        <Card className="flex flex-col">
          {!docsPending && unbillableCount === 0 && staleOrders.length === 0 && manualReviewOrders.length === 0 && duplicateCount === 0 && needsTruckCount === 0 ? (
            <CardContent className="flex flex-col items-center justify-center flex-1 text-center">
              <CheckCircle2 className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <p className="text-lg font-medium text-muted-foreground">All caught up</p>
              <p className="text-sm text-muted-foreground/70 mt-1">No unbillable orders, stale orders, or manual reviews</p>
            </CardContent>
          ) : (
            <>
              {/* Desktop (lg+): tab bar + content */}
              <div
                className="hidden lg:grid border-b shrink-0"
                style={{ gridTemplateColumns: `repeat(${insightItems.length}, minmax(0, 1fr))` }}
              >
                <div
                  className={cn(
                    "flex items-center gap-4 p-5 cursor-pointer transition-all border-b-2 rounded-tl-lg",
                    activeInsight === "duplicates"
                      ? "border-b-foreground bg-card"
                      : "border-b-transparent bg-muted/50 hover:bg-muted/80"
                  )}
                  onClick={() => setActiveInsight("duplicates")}
                >
                  <div className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                    duplicateCount > 0 ? "bg-amber-500/10" : "bg-muted",
                  )}>
                    <Copy className={cn(
                      "h-5 w-5",
                      duplicateCount > 0 ? "text-amber-500" : "text-muted-foreground",
                    )} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{duplicateCount}</p>
                    <p className="text-sm font-medium">Duplicate Orders</p>
                  </div>
                </div>

                <div
                  className={cn(
                    "flex items-center gap-4 p-5 cursor-pointer transition-all border-b-2",
                    activeInsight === "needs-truck"
                      ? "border-b-foreground bg-card"
                      : "border-b-transparent bg-muted/50 hover:bg-muted/80"
                  )}
                  onClick={() => setActiveInsight("needs-truck")}
                >
                  <div className={cn(
                    "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
                    needsTruckCount > 0 ? "bg-destructive/10" : "bg-muted",
                  )}>
                    <TruckIcon className={cn(
                      "h-5 w-5",
                      needsTruckCount > 0 ? "text-destructive" : "text-muted-foreground",
                    )} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{needsTruckCount}</p>
                    <p className="text-sm font-medium">Needs Truck Assignment</p>
                  </div>
                </div>

                <div
                  className={cn(
                    "flex items-center gap-4 p-5 cursor-pointer transition-all border-b-2",
                    activeInsight === "manual-review"
                      ? "border-b-foreground bg-card"
                      : "border-b-transparent bg-muted/50 hover:bg-muted/80"
                  )}
                  onClick={() => setActiveInsight("manual-review")}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{manualReviewOrders.length}</p>
                    <p className="text-sm font-medium">Manual Trips</p>
                  </div>
                </div>

                <div
                  className={cn(
                    "flex items-center gap-4 p-5 cursor-pointer transition-all border-b-2",
                    activeInsight === "unbillable"
                      ? "border-b-foreground bg-card"
                      : "border-b-transparent bg-muted/50 hover:bg-muted/80"
                  )}
                  onClick={() => setActiveInsight("unbillable")}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-destructive/10">
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{unbillableDisplay}</p>
                    <p className="text-sm font-medium">Unbillable Orders</p>
                  </div>
                </div>



                <div
                  className={cn(
                    "flex items-center gap-4 p-5 cursor-pointer transition-all border-b-2",
                    "rounded-tr-lg",
                    activeInsight === "stale"
                      ? "border-b-foreground bg-card"
                      : "border-b-transparent bg-muted/50 hover:bg-muted/80"
                  )}
                  onClick={() => setActiveInsight("stale")}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--aurora-status-warning))]/10">
                    <AlertTriangle className="h-5 w-5 text-[hsl(var(--aurora-status-warning))]" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{staleOrders.length}</p>
                    <p className="text-sm font-medium">Stale Orders</p>
                  </div>
                </div>

              </div>

              <CardContent className="hidden lg:block pt-6 pb-0 overflow-y-auto flex-1 min-h-0">
                {insightBody(activeInsight)}
              </CardContent>

              {/* Tablet & mobile (<lg): stacked accordion */}
              <div className="lg:hidden">
                <Accordion type="single" collapsible className="w-full">
                  {insightItems.map((item, idx) => {
                    const Icon = item.icon;
                    return (
                      <AccordionItem
                        key={item.key}
                        value={item.key}
                        className={cn("border-b", idx === 0 && "border-t-0")}
                      >
                        <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-muted/50 data-[state=open]:bg-card">
                          <div className="flex items-center gap-4 flex-1">
                            <div className={cn("flex h-10 w-10 shrink-0 items-center justify-center rounded-full", item.iconBg)}>
                              <Icon className={cn("h-5 w-5", item.iconColor)} />
                            </div>
                            <p className="text-2xl font-bold leading-none">{item.count}</p>
                            <p className="text-sm font-medium text-left">{item.label}</p>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-5 pt-2 pb-5">
                          {insightBody(item.key)}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            </>
          )}
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 shrink-0">
          <LateOrdersTable
            title="Late departures"
            variant="departure"
            orders={lateDepartures}
            onRowClick={(o) => setPreviewOrder(o as unknown as StarredOrderWithRelations)}
            selectedId={previewOrder?.id}
            columns={1}
            compactEmpty
          />

          <LateOrdersTable
            title="Late arrivals"
            variant="arrival"
            orders={lateArrivals}
            onRowClick={(o) => setPreviewOrder(o as unknown as StarredOrderWithRelations)}
            selectedId={previewOrder?.id}
            columns={1}
            compactEmpty
          />
        </div>

        {/* Subscribed Orders */}
        {starredIdArray.length > 0 && (
          <Card className="shrink-0">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <span className="font-medium text-lg">Subscribed orders</span>
              </div>
              <ResponsiveTableShell
                cards={
                  <div className="grid grid-cols-1 gap-3">
                    {starredPagination.pageRows.map((o) => (
                      <OrderCard
                        key={o.id}
                        order={o as any}
                        selected={previewOrder?.id === o.id}
                        isChecked={false}
                        onToggle={() => {}}
                        onClick={() => handleStarredRowClick(o)}
                        onEdit={(order) => setEditOrder(order as unknown as StarredOrderWithRelations)}
                        locations={locations}
                        legs={starredLegsFor(o.id)}
                      />
                    ))}
                  </div>
                }
                table={
                  <div className="rounded-lg border bg-background overflow-x-auto">
                    <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px] text-muted-foreground" />
                      <TableHead className="text-muted-foreground whitespace-nowrap">Order ID</TableHead>
                      <TableHead className="text-muted-foreground">Status</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Trip Type</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Ship From</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Ship To</TableHead>
                      <TableHead className="text-muted-foreground">Tractor</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Trailer ID</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Trailer Type</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Tender ID</TableHead>
                      <TableHead className="text-muted-foreground whitespace-nowrap">Billing Party</TableHead>
                      <TableHead className="text-muted-foreground">Operator</TableHead>
                      <TableHead className="text-muted-foreground w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {starredOrders?.length ? (
                      starredPagination.pageRows.map((o) => (
                        <OrderTableRow
                          key={o.id}
                          order={o as any}
                          showCheckbox={false}
                          showLoadStatus={false}
                          locations={locations}
                          legs={starredLegsFor(o.id)}
                          isSelected={previewOrder?.id === o.id}
                          onRowClick={() => handleStarredRowClick(o)}
                          actionsSlot={
                            <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                              <BolViewerDialog orderId={o.id} orderUniqueId={o.unique_id} />
                              <TenderViewerDialog orderId={o.id} orderUniqueId={o.unique_id} />
                            </div>
                          }
                        />
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={13} className="text-center py-6 text-muted-foreground text-sm">
                          Loading…
                        </TableCell>
                      </TableRow>
                    )}
                    </TableBody>
                  </Table>
                  </div>
                }
              />
              <DashboardPagination
                page={starredPagination.page}
                totalPages={starredPagination.totalPages}
                onPageChange={starredPagination.setPage}
                rangeStart={starredPagination.rangeStart}
                rangeEnd={starredPagination.rangeEnd}
                total={starredPagination.total}
              />
            </CardContent>
          </Card>
        )}

        <OrderDetailSheet
          order={previewOrder}
          open={!!previewOrder}
          onOpenChange={(open) => { if (!open) setPreviewOrder(null); }}
          onEdit={(order) => {
            setPreviewOrder(null);
            setEditOrder(order as unknown as StarredOrderWithRelations);
          }}
          onView={(order) => {
            const targetId = (order as any).parent_order_id || order.id;
            const legParam = (order as any).parent_order_id ? `?leg=${order.id}` : "";
            setPreviewOrder(null);
            navigate(`/orders/${targetId}${legParam}`);
          }}
        />

        <OrderEditDialog
          order={editOrder as any}
          open={!!editOrder}
          onOpenChange={(open) => { if (!open) setEditOrder(null); }}
        />
      </div>
    </AppLayout>
  );
}
